import { useEffect, useState } from 'react';
import { useSocket } from '@/hooks/useSocket';
import { useOnboardingStore } from '@/stores/onboardingStore';
import {
  AUTOMATION_EVENTS,
  type AutomationProgressPayload,
  type AutomationStageCompletePayload,
  type AutomationCompletePayload,
  type AutomationErrorPayload,
} from '@/services/socket/socketEvents';

/**
 * Subscribes to all automation pipeline Socket.IO events and
 * keeps the onboarding store in sync.
 *
 * Usage:
 * ```tsx
 * const { isRunning, stages, isComplete, error, summary } = useAutomationSocket();
 * ```
 */
export function useAutomationSocket() {
  const { isConnected, connectionId, on, off } = useSocket();

  const automation = useOnboardingStore(state => state.automation);
  const updateAutomationStage = useOnboardingStore(state => state.updateAutomationStage);
  const completeAutomation = useOnboardingStore(state => state.completeAutomation);
  const setAutomationError = useOnboardingStore(state => state.setAutomationError);

  const [summary, setSummary] = useState<AutomationCompletePayload['summary'] | null>(null);

  useEffect(() => {
    if (!isConnected) return;

    const handleProgress = (data: AutomationProgressPayload) => {
      updateAutomationStage({
        stage: data.stage,
        status: data.status,
        progress: data.progress,
        message: data.message,
      });
    };

    const handleStageComplete = (data: AutomationStageCompletePayload) => {
      updateAutomationStage({
        stage: data.stage,
        status: 'completed',
        completedAt: data.completedAt,
      });
    };

    const handleComplete = (data: AutomationCompletePayload) => {
      setSummary(data.summary ?? null);
      completeAutomation();
    };

    const handleError = (data: AutomationErrorPayload) => {
      setAutomationError({
        stage: data.stage,
        message: data.error,
        recoverable: data.recoverable,
      });
    };

    on<AutomationProgressPayload>(AUTOMATION_EVENTS.PROGRESS, handleProgress);
    on<AutomationStageCompletePayload>(AUTOMATION_EVENTS.STAGE_COMPLETE, handleStageComplete);
    on<AutomationCompletePayload>(AUTOMATION_EVENTS.COMPLETE, handleComplete);
    on<AutomationErrorPayload>(AUTOMATION_EVENTS.ERROR, handleError);

    return () => {
      off<AutomationProgressPayload>(AUTOMATION_EVENTS.PROGRESS, handleProgress);
      off<AutomationStageCompletePayload>(AUTOMATION_EVENTS.STAGE_COMPLETE, handleStageComplete);
      off<AutomationCompletePayload>(AUTOMATION_EVENTS.COMPLETE, handleComplete);
      off<AutomationErrorPayload>(AUTOMATION_EVENTS.ERROR, handleError);
    };
  }, [
    isConnected,
    connectionId,
    on,
    off,
    updateAutomationStage,
    completeAutomation,
    setAutomationError,
  ]);

  return {
    isRunning: automation.isRunning,
    stages: automation.stages,
    isComplete:
      !automation.isRunning &&
      Array.isArray(automation.stages) &&
      automation.stages.length > 0 &&
      automation.stages.every(s => s.status === 'completed' || s.status === 'skipped'),
    error: automation.error,
    summary,
  };
}
